import { useState } from 'react';
import { format } from 'date-fns';
import { Ellipsis, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DeleteDialog } from '@/components/shared/delete-dialog';
import { toast } from '@/components/ui/sonner';
import { useDeletePersona } from '@/hooks/use-personas';
import { cn } from '@/lib/utils';
import { PERSONA_STATUS_CONFIG } from '../constants';
import { PersonaRegenerateModal } from './persona-regenerate-modal';
import type { Persona } from '@/types';

interface PersonaCardProps {
	persona: Persona;
	name: string;
	description: string;
	onClick: (persona: Persona) => void;
}

function initials(name: string): string {
	return name
		.split(' ')
		.map((part) => part[0])
		.filter(Boolean)
		.slice(0, 2)
		.join('')
		.toUpperCase();
}

export function PersonaCard({ persona, name, description, onClick }: PersonaCardProps) {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const deletePersona = useDeletePersona();
	const isGenerating = persona.generationStatus === 'generating';
	const isFirstGeneration = isGenerating && persona.currentVersion === '0';

	const handleDelete = async () => {
		try {
			await deletePersona.mutateAsync({ id: persona.id });
			toast.success('Persona deleted');
			setDeleteDialogOpen(false);
		} catch {
			toast.error('Failed to delete persona');
		}
	};

	const statusConfig = PERSONA_STATUS_CONFIG[persona.validationStatus];
	const isAgent = persona.userId != null;

	const statusRow = (
		<div className='flex items-center gap-2'>
			{isGenerating ? (
				<Badge variant='secondary' className='w-fit gap-1'>
					<Loader2 className='size-3 animate-spin' />
					Generating
				</Badge>
			) : (
				<Badge variant={statusConfig.badgeVariant} className='w-fit'>
					{statusConfig.label}
				</Badge>
			)}
			{!isGenerating && persona.validationStatus === 'validated' && persona.lastValidatedAt && (
				<p className='text-[11px] italic text-muted-foreground whitespace-nowrap'>
					Last Validated on {format(new Date(persona.lastValidatedAt), 'dd/MM/yyyy')}
				</p>
			)}
		</div>
	);

	return (
		<Card
			className={cn(
				'transition-shadow',
				isFirstGeneration ? 'cursor-default' : 'cursor-pointer hover:drop-shadow-lg hover:drop-shadow-black/5 hover:bg-gray-50',
				isAgent ? 'w-[322px] rounded-3xl' : 'w-full max-w-[477px] rounded-[20px]'
			)}
			onClick={isFirstGeneration ? undefined : () => onClick(persona)}
		>
			<CardContent className='flex flex-col gap-3'>
				{isAgent ? (
					<>
						<div className='flex items-start justify-between'>
							<Avatar className='size-16 border'>
								<AvatarFallback className='text-base font-medium'>{initials(name)}</AvatarFallback>
							</Avatar>
							<PersonaCardMenu
								persona={persona}
								name={name}
								isGenerating={isGenerating}
								onDelete={() => setDeleteDialogOpen(true)}
							/>
						</div>
						<div className='flex flex-col gap-1'>
							<h3 className='font-medium text-base line-clamp-1'>{name}</h3>
							<p className='text-xs text-muted-foreground line-clamp-3'>{description}</p>
						</div>
						{statusRow}
					</>
				) : (
					<>
						<div className='flex items-center justify-between gap-2'>
							<h3 className='font-medium text-base line-clamp-1'>{name}</h3>
							<PersonaCardMenu
								persona={persona}
								name={name}
								isGenerating={isGenerating}
								onDelete={() => setDeleteDialogOpen(true)}
							/>
						</div>
						<p className='text-xs text-muted-foreground line-clamp-3'>{description}</p>
						{statusRow}
					</>
				)}
			</CardContent>

			<DeleteDialog
				open={deleteDialogOpen}
				onOpenChange={setDeleteDialogOpen}
				onConfirm={handleDelete}
				title='Delete Persona'
				description='Are you sure you want to delete this persona? This action cannot be undone.'
				itemName={name}
				isLoading={deletePersona.isPending}
			/>
		</Card>
	);
}

function PersonaCardMenu({
	persona,
	name,
	isGenerating,
	onDelete,
}: {
	persona: Persona;
	name: string;
	isGenerating: boolean;
	onDelete: () => void;
}) {
	const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						className='cursor-pointer bg-inherit border-none shadow-none'
						variant='outline'
						size='icon'
						onClick={(e) => e.stopPropagation()}
					>
						<Ellipsis className='h-3 w-3' />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align='end' onClick={(e) => e.stopPropagation()}>
					<DropdownMenuItem variant='default' disabled={isGenerating} onSelect={() => setRegenerateModalOpen(true)}>
						<RefreshCw className='h-4 w-4 mr-2' />
						Re-Generate
					</DropdownMenuItem>
					<DropdownMenuItem variant='destructive' disabled={isGenerating} onSelect={() => onDelete()}>
						<Trash2 className='h-4 w-4 mr-2' />
						Delete
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<PersonaRegenerateModal
				personaId={persona.id}
				label={name}
				open={regenerateModalOpen}
				onOpenChange={setRegenerateModalOpen}
			/>
		</>
	);
}
